import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings, Info, ChevronLeft } from 'lucide-react'; // Lock e UpgradeButton removidos
import i18n from '../../i18n'; // Importando i18n para acessar a lista de idiomas
import { saveAgentConfig, fetchAgentConfig } from '../../services/aiConfigService';
import toast from 'react-hot-toast';
// useAuth e UpgradeButton não são necessários aqui, pois este agente não é exclusivo PRO.

const modelOptions = ['Gemini 3.8 Flash', 'Gemini 3.1 Pro'];
const toneOptions = ['direct', 'persuasive', 'curiosity', 'consultative', 'urgency'] as const;
const timezoneOptions = [
  // GMT-8 a GMT-3 (Américas)
  'America/Los_Angeles (GMT-8)',
  'America/Phoenix (GMT-7)',
  'America/Mexico_City (GMT-6)',
  'America/Costa_Rica (GMT-6)',
  'America/Bogota (GMT-5)',
  'America/Lima (GMT-5)',
  'America/Panama (GMT-5)',
  'America/Guayaquil (GMT-5)',
  'America/Havana (GMT-5)',
  'America/New_York (GMT-5)',
  'America/Caracas (GMT-4)',
  'America/La_Paz (GMT-4)',
  'America/Santo_Domingo (GMT-4)',
  'America/Puerto_Rico (GMT-4)',
  'America/Santiago (GMT-4)',
  'America/Asuncion (GMT-4)',
  'America/Sao_Paulo (GMT-3)',
  'America/Argentina/Buenos_Aires (GMT-3)',
  'America/Montevideo (GMT-3)',

  // Europa
  'Europe/Lisbon (GMT+0)',
  'Europe/London (GMT+0)',
  'Europe/Madrid (GMT+1)',
  'Europe/Paris (GMT+1)',

  // Ásia e Oceania
  'Asia/Tokyo (GMT+9)',
  'Australia/Sydney (GMT+10)',
];

const BulkSendAgentConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'model' | 'prompt'>('model'); // Removendo 'full_prompt'
  const [isLoading, setIsLoading] = useState(true);

  // Model Config States
  const [temperature, setTemperature] = useState(0.5);
  const [selectedModel, setSelectedModel] = useState(modelOptions[0]);
  const [knowledgeRestriction, setKnowledgeRestriction] = useState(false);
  const [languageDetector, setLanguageDetector] = useState(true);
  const [timezoneSetting, setTimezoneSetting] = useState(t('ai.config.timezone_include'));
  const [selectedTimezone, setSelectedTimezone] = useState(timezoneOptions[0]);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  // Prompt Config States (Bulk Send specific)
  const [companyName, setCompanyName] = useState('');
  const [yourName, setYourName] = useState('');
  const [productName, setProductName] = useState('');
  const [painPoint, setPainPoint] = useState('');
  const [transformation, setTransformation] = useState('');
  const [cta, setCta] = useState('');
  const [messageTone, setMessageTone] = useState<typeof toneOptions[number]>('direct');

  // Full Prompt State (Mantido apenas para carregar/salvar, mas não exibido)
  const [fullPrompt, setFullPrompt] = useState('');


  const availableLanguages = [
    { code: 'pt-BR', name: t('languages.pt-BR') },
    { code: 'es', name: t('languages.es') },
    { code: 'en', name: t('languages.en') },
    { code: 'fr', name: t('languages.fr') },
  ];

  // --- Carregamento Inicial ---
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await fetchAgentConfig('bulk_send');
        if (config) {
          // Carregar Model Config
          if (config.model_config) {
            setTemperature(config.model_config.temperature ?? 0.5);
            setSelectedModel(config.model_config.selectedModel ?? modelOptions[0]);
            setKnowledgeRestriction(config.model_config.knowledgeRestriction ?? false);
            setLanguageDetector(config.model_config.languageDetector ?? true);
            setTimezoneSetting(config.model_config.timezoneSetting ?? t('ai.config.timezone_include'));
            setSelectedTimezone(config.model_config.selectedTimezone ?? timezoneOptions[0]);
            setSelectedLanguage(config.model_config.selectedLanguage ?? i18n.language);
          }
          // Carregar Prompt Config
          if (config.prompt_config) {
            setCompanyName(config.prompt_config.companyName ?? '');
            setYourName(config.prompt_config.yourName ?? '');
            setProductName(config.prompt_config.productName ?? '');
            setPainPoint(config.prompt_config.painPoint ?? '');
            setTransformation(config.prompt_config.transformation ?? '');
            setCta(config.prompt_config.cta ?? '');
            setMessageTone(config.prompt_config.messageTone ?? 'direct');
          }
          // Carregar Full Prompt
          setFullPrompt(config.full_prompt ?? '');
        }
      } catch (error) {
        toast.error(t('Falha ao carregar configurações do agente.'));
      } finally {
        setIsLoading(false);
      }
    };
    loadConfig();
  }, [t]);


  const handleSave = async () => {
    setIsLoading(true);

    const model_config = {
      temperature,
      selectedModel,
      knowledgeRestriction,
      languageDetector,
      timezoneSetting,
      selectedTimezone,
      selectedLanguage,
    };

    const prompt_config = {
      companyName,
      yourName,
      productName,
      painPoint,
      transformation,
      cta,
      messageTone,
    };

    try {
      await saveAgentConfig({
        agent_type: 'bulk_send',
        model_config,
        prompt_config,
        full_prompt: fullPrompt, // Mantendo o campo para salvar o estado atual
      });
      toast.success(t('Configurações do Agente de Disparo salvas com sucesso!'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro ao salvar configurações.');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderModelConfig = () => (
    <div className="space-y-8">
      {/* Modelo */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="model-select">{t('ai.config.model_label')}</label>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="relative">
            <select
              className="w-full appearance-none rounded-lg border-gray-300 bg-gray-100 dark:bg-gray-800 dark:border-gray-700 py-3 pl-4 pr-10 focus:border-primary focus:ring-primary dark:text-white"
              id="model-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
            >
              {modelOptions.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
            <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">expand_more</span>
          </div>
          <motion.button
            type="button"
            className="flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Settings className="size-4" />
            <span>{t('ai.config.model_settings_button')}</span>
          </motion.button>
        </div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('ai.config.model_description')}</p>
      </div>

      {/* Temperatura */}
      <div>
        <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="temperature-slider">
          <span>{t('ai.config.temperature_label')}</span>
          <span title={t('ai.config.temperature_tooltip')}>
            <Info className="size-4 text-gray-400" />
          </span>
        </label>
        <div className="mt-4">
          <input
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary"
            id="temperature-slider"
            max="1"
            min="0"
            step="0.1"
            type="range"
            value={temperature}
            onChange={(e) => setTemperature(Number(e.target.value))}
          />
          <div className="mt-2 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            {Array.from({ length: 11 }, (_, i) => (
              <span key={i}>{i / 10}</span>
            ))}
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('ai.config.temperature_description')}</p>
      </div>

      {/* Comportamento */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('ai.config.behavior_title')}</h3>

        {/* Restrição de conhecimento */}
        <div className="relative flex items-start">
          <div className="flex h-6 items-center">
            <input
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:bg-gray-700 dark:border-gray-600"
              id="knowledge-restriction"
              name="knowledge-restriction"
              type="checkbox"
              checked={knowledgeRestriction}
              onChange={() => setKnowledgeRestriction(!knowledgeRestriction)}
            />
          </div>
          <div className="ml-3 text-sm leading-6">
            <label className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-200" htmlFor="knowledge-restriction">
              <span>{t('ai.config.knowledge_restriction_label')}</span>
              <span title={t('ai.config.knowledge_restriction_tooltip')}>
                <Info className="size-4 text-gray-400" />
              </span>
            </label>
            <p className="text-gray-500 dark:text-gray-400" id="knowledge-restriction-description">{t('ai.config.knowledge_restriction_description')}</p>
          </div>
        </div>

        {/* Detector de Idioma */}
        <div className="relative flex items-start">
          <div className="flex h-6 items-center">
            <input
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:bg-gray-700 dark:border-gray-600"
              id="language-detector"
              name="language-detector"
              type="checkbox"
              checked={languageDetector}
              onChange={() => setLanguageDetector(!languageDetector)}
            />
          </div>
          <div className="ml-3 text-sm leading-6">
            <label className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-200" htmlFor="language-detector">
              <span>{t('ai.config.language_detector_label')}</span>
              <span title={t('ai.config.language_detector_tooltip')}>
                <Info className="size-4 text-gray-400" />
              </span>
            </label>
            <p className="text-gray-500 dark:text-gray-400" id="language-detector-description">{t('ai.config.language_detector_description')}</p>
          </div>
        </div>
      </div>

      {/* Fuso Horário */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="timezone-select">{t('ai.config.timezone_label')}</label>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="relative">
            <select
              className="w-full appearance-none rounded-lg border-gray-300 bg-gray-100 dark:bg-gray-800 dark:border-gray-700 py-3 pl-4 pr-10 focus:border-primary focus:ring-primary dark:text-white"
              id="timezone-toggle"
              value={timezoneSetting}
              onChange={(e) => setTimezoneSetting(e.target.value)}
            >
              <option>{t('ai.config.timezone_include')}</option>
              <option>{t('ai.config.timezone_exclude')}</option>
            </select>
            <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">expand_more</span>
          </div>
          <div className="relative">
            <select
              className="w-full appearance-none rounded-lg border-gray-300 bg-gray-100 dark:bg-gray-800 dark:border-gray-700 py-3 pl-4 pr-10 focus:border-primary focus:ring-primary dark:text-white"
              id="timezone-select"
              value={selectedTimezone}
              onChange={(e) => setSelectedTimezone(e.target.value)}
              disabled={timezoneSetting === t('ai.config.timezone_exclude')}
            >
              {timezoneOptions.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">expand_more</span>
          </div>
        </div>
      </div>

      {/* Idioma do Agente */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="agent-language-select">{t('ai.config.agent_language_label')}</label>
        <div className="relative mt-2 max-w-xs">
          <select
            className="w-full appearance-none rounded-lg border-gray-300 bg-gray-100 dark:bg-gray-800 dark:border-gray-700 py-3 pl-4 pr-10 focus:border-primary focus:ring-primary dark:text-white"
            id="agent-language-select"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            {availableLanguages.map(lang => (
              <option key={lang.code} value={lang.code}>{lang.name}</option>
            ))}
          </select>
          <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">expand_more</span>
        </div>
      </div>
    </div>
  );

  const renderPromptConfig = () => (
    <div className="space-y-8">
      {/* Sobre Você (Quem envia) */}
      <div className="space-y-6 rounded-lg bg-gray-50/50 dark:bg-gray-900/20 p-6 border border-gray-200 dark:border-gray-700/50">
        <h3 className="text-lg font-semibold text-[#181411] dark:text-white">{t('ai.prompt.bulk_sender_title')}</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="company-name">{t('ai.prompt.bulk_company_name')}</label>
            <input
              className="mt-1 block w-full rounded-lg border-gray-300 bg-white dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 p-2.5 transition-colors"
              id="company-name"
              placeholder={t('ai.prompt.bulk_company_placeholder')}
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="your-name">{t('ai.prompt.bulk_your_name')}</label>
            <input
              className="mt-1 block w-full rounded-lg border-gray-300 bg-white dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 p-2.5 transition-colors"
              id="your-name"
              placeholder={t('ai.prompt.bulk_your_name_placeholder')}
              type="text"
              value={yourName}
              onChange={(e) => setYourName(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Sobre sua Oferta (O que você vende) */}
      <div className="space-y-6 rounded-lg bg-gray-50/50 dark:bg-gray-900/20 p-6 border border-gray-200 dark:border-gray-700/50">
        <h3 className="text-lg font-semibold text-[#181411] dark:text-white">{t('ai.prompt.bulk_offer_title')}</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="product-name">{t('ai.prompt.bulk_product_name')}</label>
          <input
            className="mt-1 block w-full rounded-lg border-gray-300 bg-white dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 p-2.5 transition-colors"
            id="product-name"
            placeholder={t('ai.prompt.bulk_product_placeholder')}
            type="text"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="pain-point">{t('ai.prompt.bulk_pain_point')}</label>
          <textarea
            className="mt-1 block w-full rounded-lg border-gray-300 bg-white dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 p-2.5 transition-colors"
            id="pain-point"
            placeholder={t('ai.prompt.bulk_pain_point_placeholder')}
            rows={2}
            value={painPoint}
            onChange={(e) => setPainPoint(e.target.value)}
          ></textarea>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('ai.prompt.bulk_pas_problem')}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="transformation">{t('ai.prompt.bulk_transformation')}</label>
          <textarea
            className="mt-1 block w-full rounded-lg border-gray-300 bg-white dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 p-2.5 transition-colors"
            id="transformation"
            placeholder={t('ai.prompt.bulk_transformation_placeholder')}
            rows={2}
            value={transformation}
            onChange={(e) => setTransformation(e.target.value)}
          ></textarea>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('ai.prompt.bulk_pas_solution')}</p>
        </div>
      </div>

      {/* Seu Objetivo (O que você quer) */}
      <div className="space-y-6 rounded-lg bg-gray-50/50 dark:bg-gray-900/20 p-6 border border-gray-200 dark:border-gray-700/50">
        <h3 className="text-lg font-semibold text-[#181411] dark:text-white">{t('ai.prompt.bulk_objective_title')}</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="cta">{t('ai.prompt.bulk_cta')}</label>
          <input
            className="mt-1 block w-full rounded-lg border-gray-300 bg-white dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 p-2.5 transition-colors"
            id="cta"
            placeholder={t('ai.prompt.bulk_cta_placeholder')}
            type="text"
            value={cta}
            onChange={(e) => setCta(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="message-tone">{t('ai.prompt.bulk_tone_label')}</label>
          <div className="relative mt-1 max-w-sm">
            <select
              className="w-full appearance-none rounded-lg border-gray-300 bg-white dark:bg-background-dark-elevated2 dark:border-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary dark:focus:border-primary/70 dark:focus:ring-primary/20 p-2.5 pr-10 transition-colors"
              id="message-tone"
              value={messageTone}
              onChange={(e) => setMessageTone(e.target.value as typeof toneOptions[number])}
            >
              {toneOptions.map(tone => (
                <option key={tone} value={tone}>{t(`ai.prompt.bulk_tone_${tone}`)}</option>
              ))}
            </select>
            <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">expand_more</span>
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t(`ai.prompt.bulk_tone_${messageTone}_description`)}</p>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 p-6 lg:p-10 max-w-7xl mx-auto">
        <span className="material-symbols-outlined text-4xl text-primary animate-spin">sync</span>
        <p className="ml-4 text-lg text-gray-600 dark:text-gray-400">{t('dashboard.loading')}</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="p-6 lg:p-10 max-w-7xl mx-auto"
    >
      <div className="mx-auto max-w-4xl">

        {/* Header e Botão Voltar */}
        <div className="mb-8 flex items-center gap-4">
          <Link to="/ia">
            <motion.button
              className="flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronLeft className="size-5" />
              {t('ai.config.back_to_ai')}
            </motion.button>
          </Link>
        </div>

        <div className="mb-8">
          <p className="text-[#181411] dark:text-white text-4xl font-black leading-tight tracking-[-0.033em]">{t('ai.config.bulk_send_title')}</p>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">{t('ai.config.bulk_send_subtitle')}</p>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/50 p-6 sm:p-8 card">

          {/* Tabs */}
          <div className="mb-6 border-b border-gray-200 dark:border-gray-700/50">
            <div aria-label="Tabs" className="flex -mb-px">
              <motion.button
                onClick={() => setActiveTab('model')}
                className={`relative whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'model'
                  ? 'text-primary border-primary'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                  } mr-8`}
                whileHover={{ y: activeTab === 'model' ? 0 : -2 }}
              >
                {t('ai.config.tab_model')}
                {activeTab === 'model' && (
                  <motion.span
                    className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-orange-400 to-primary"
                    layoutId="ai-tab-underline"
                  />
                )}
              </motion.button>

              <motion.button
                onClick={() => setActiveTab('prompt')}
                className={`relative whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'prompt'
                  ? 'text-primary border-primary'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                whileHover={{ y: activeTab === 'prompt' ? 0 : -2 }}
              >
                {t('ai.config.tab_prompt')}
                {activeTab === 'prompt' && (
                  <motion.span
                    className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-orange-400 to-primary"
                    layoutId="ai-tab-underline"
                  />
                )}
              </motion.button>
            </div>
          </div>

          {/* Conteúdo das Abas */}
          <div className="min-h-64">
            {activeTab === 'model' && renderModelConfig()}
            {activeTab === 'prompt' && renderPromptConfig()}
          </div>

          {/* Botão Salvar */}
          <div className="mt-8 flex justify-end">
            <motion.button
              onClick={handleSave}
              disabled={isLoading}
              className="flex items-center justify-center gap-2 rounded-lg bg-gradient-to-br from-orange-400 to-primary px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:from-orange-500 hover:to-orange-600 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: isLoading ? 1 : 1.05 }}
              whileTap={{ scale: isLoading ? 1 : 0.95 }}
            >
              <span className="material-symbols-outlined text-lg">save</span>
              <span>{t('sidebar.save_changes')}</span>
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BulkSendAgentConfigPage;